/* ============================================================
   MY AGENT — SENARAI (PROTOTAIP)
   ============================================================
   SATU takrifan, DUA tempat memakainya:

     · hub PRO Studio        — di dalam panel "For the SMART module only"
     · /prostudio-v3.9/agent — halaman senarai penuh, destinasi pautan
                               daripada Control Panel tab Agent AI

   ⛔ Sebab itu ia wujud sebagai fail sendiri. Kalau baris ini disalin
      ke dua tempat, dua senarai yang sepatutnya sama akan terpesong
      sebaik salah satu diubah — dan pelanggan nampak status berbeza
      bagi agent yang sama, bergantung dari mana dia datang.

   `padam` — pilihan. Hub tidak memberinya (senarai di sana untuk
   melihat dan masuk sahaja); halaman penuh yang memberinya. Tanpa
   prop itu ikon tong sampah langsung tidak dirender.

   `onTogolWorkspace` — pilihan (arahan user 2026-10-18). Beri ia dan setiap
   baris dapat toggle "Papar di Workspace": OFF = agent disorok daripada
   pemilih agent Workspace, tidak dipadam. Dipanggil (id, paparBaharu);
   menyimpannya kerja pemanggil. Tiada prop = tiada toggle.

   Rupa minimal ala cal.com (arahan user 2026-10-18): tiada ikon robot,
   tiada lencana "Ready" (hanya "Not configured" yang kekal), tiada chevron —
   klik baris sahaja sudah membuka agent. Tong sampah masuk menu "···".

   `onSusun` — pilihan. Beri ia dan baris boleh disusun semula. SATU kaedah
   bagi setiap jenis peranti, bukan kedua-dua sekali (arahan user 2026-08-19):
   peranti bertetikus dapat pemegang + tarik-lepas SAHAJA, peranti sentuh dapat
   anak panah naik/turun SAHAJA (tarik-lepas HTML5 mati di sana — lihat nota
   dalam utils/susunAgent.js). Ia dipanggil dengan senarai id dalam susunan
   BAHARU; menyimpannya kerja pemanggil, kerana hub menyimpan ke Firestore
   manakala mod pelan hanya menyusun data contoh dalam ingatan.
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, MoreHorizontal, Trash2 } from 'lucide-react';

import '../design/tokens.css';
import { Badge, IconBtn, List, Row } from '../design/ui';
import { useT } from '../i18n';
import { alihItem, bolehTarikTetikus, sisipItem } from '../utils/susunAgent';

export default function SenaraiAgentV39({ agents = [], onBuka, padam, onSusun, onTogolWorkspace }) {
    const t = useT();

    /* Menu "···" — kedudukan TETAP (`.s-menu`), jadi ia tidak terpotong oleh
       bucu `.s-list` yang ber-overflow hidden. Ditutup bila apa-apa menatal,
       sama seperti menu dalam ProductsV39View. */
    const [menu, setMenu] = useState(null);     // { id, x, y }
    useEffect(() => {
        if (!menu) return undefined;
        const tutup = () => setMenu(null);
        window.addEventListener('scroll', tutup, true);
        window.addEventListener('resize', tutup);
        return () => {
            window.removeEventListener('scroll', tutup, true);
            window.removeEventListener('resize', tutup);
        };
    }, [menu]);
    const bukaMenu = (e, id) => {
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        setMenu({ id, x: Math.max(8, Math.min(r.right - 190, window.innerWidth - 198)), y: r.bottom + 4 });
    };

    /* Baris yang sedang ditarik, dan celah tempat ia akan mendarat.
       Kedua-duanya keadaan PAPARAN sahaja — susunan sebenar hidup pada
       pemanggil, yang juga yang menyimpannya. */
    const [tarikId, setTarikId] = useState(null);
    const [sasar, setSasar] = useState(null);   // { id, bawah }

    /* Disemak SEKALI: ia sifat peranti, bukan sesuatu yang berubah semasa
       pelanggan menggunakan halaman. */
    const adaTetikus = useMemo(() => bolehTarikTetikus(), []);

    /* Satu agent tidak boleh disusun. Merender pemegang tarik dan dua anak
       panah mati pada senarai satu baris hanya menambah kawalan yang tidak
       membuat apa-apa. */
    const bolehSusun = typeof onSusun === 'function' && agents.length > 1;
    const ids = agents.map((a) => a.id);

    const naikTurun = (dari, ke) => {
        if (ke < 0 || ke >= ids.length) return;
        onSusun(alihItem(ids, dari, ke));
    };

    const lepasPada = (idSasar, bawah) => {
        const dari = ids.indexOf(tarikId);
        const atas = ids.indexOf(idSasar);
        setTarikId(null);
        setSasar(null);
        if (dari < 0 || atas < 0) return;
        onSusun(sisipItem(ids, dari, bawah ? atas + 1 : atas));
    };

    return (
        <List>
            {agents.map((a, i) => {
                const ditarik = tarikId === a.id;
                const tandaJatuh = !!tarikId && tarikId !== a.id && sasar?.id === a.id;
                /* Tanda "Simpan N gambar" (arahan user 2026-09-13) — tanda yang SAMA seperti
                   pemilih agent di Workspace, jadi dua senarai itu memakai ayat yang sama
                   (kunci kamus dikongsi dengan sengaja). Ia sifat agent seperti set/babak,
                   jadi ia menyusul di hujung baris ringkasan. Agent tanpa pusingan gambar =
                   TIADA tanda. Nilai tiada/rosak (data contoh mod pelan) = 0. */
                const bilGambar = Math.max(0, Math.floor(Number(a.data?.gambar) || 0));
                /* Sama dengan agentDiWorkspace: belum Configure = AUTO OFF (arahan user
                   2026-10-18); selain itu tiada medan = papar. Semakan `configured` diulang
                   di sini kerana data contoh mod pelan tidak membawa `diWorkspace`. */
                const disorok = !!onTogolWorkspace && (!a.configured || a.diWorkspace === false);
                const barisMeta = [
                    a.configured
                        ? t('prostudio.wizard.rowMeta', {
                            set: a.data.set,
                            babak: a.data.babak,
                            hasil: a.data.hasil === 'gambarvideo'
                                ? t('prostudio.wizard.outImageVideo')
                                : t('prostudio.wizard.outImageOnly'),
                        })
                        : t('prostudio.wizard.rowUnfinished'),
                    bilGambar > 0
                        ? t(bilGambar > 1 ? 'ws.ap.savesImagesMany' : 'ws.ap.savesImagesOne', { n: bilGambar })
                        : null,
                    /* Agent belum siap sudah bertanda "Not finished" — tidak diulang. */
                    disorok && a.configured ? t('prostudio.wizard.hiddenFromWs') : null,
                ].filter(Boolean).join(' · ');
                return (
                    <Row
                        key={a.id}
                        onClick={() => onBuka?.(a)}
                        draggable={bolehSusun && adaTetikus}
                        onDragStart={(e) => {
                            setTarikId(a.id);
                            e.dataTransfer.effectAllowed = 'move';
                            /* Sebahagian browser membatalkan tarikan yang tiada
                               data sama sekali. */
                            try { e.dataTransfer.setData('text/plain', a.id); } catch { /* abaikan */ }
                        }}
                        onDragEnd={() => { setTarikId(null); setSasar(null); }}
                        onDragOver={(e) => {
                            if (!tarikId || tarikId === a.id) return;
                            /* preventDefault WAJIB — tanpanya browser menolak
                               lepasan dan onDrop tidak pernah dipanggil. */
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            const r = e.currentTarget.getBoundingClientRect();
                            const bawah = e.clientY > r.top + r.height / 2;
                            setSasar((prev) => (prev?.id === a.id && prev.bawah === bawah
                                ? prev
                                : { id: a.id, bawah }));
                        }}
                        onDrop={(e) => {
                            if (!tarikId || tarikId === a.id) return;
                            e.preventDefault();
                            const r = e.currentTarget.getBoundingClientRect();
                            lepasPada(a.id, e.clientY > r.top + r.height / 2);
                        }}
                        style={{
                            cursor: onBuka ? 'pointer' : 'default',
                            opacity: ditarik ? 0.45 : 1,
                            /* Garis pendaratan. Bayang DALAM, bukan border —
                               border menambah tinggi baris, jadi seluruh senarai
                               teranjak setiap kali kursor bergerak. */
                            boxShadow: tandaJatuh
                                ? `inset 0 ${sasar.bawah ? -2 : 2}px 0 var(--s-select)`
                                : undefined,
                            /* Dua kumpulan yang dibenarkan berpatah ke dua baris.
                               ⚠️ WAJIB: pada lebar telefon, lencana status + dua
                               anak panah + chevron memakan lebih daripada lebar
                               skrin, jadi nama diperah sampai SATU AKSARA
                               sebaris dan baris itu menjadi 231px tinggi (diukur).
                               Dengan patah baris, kawalan turun ke baris kedua
                               dan nama dapat lebar penuh. Pada komputer tiada
                               apa berubah — kedua-dua kumpulan muat sebaris. */
                            flexWrap: 'wrap',
                            rowGap: 8,
                        }}
                    >
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            flex: '1 1 200px', minWidth: 0,
                        }}>
                            {bolehSusun && adaTetikus && (
                                <span
                                    title={t('prostudio.wizard.dragHandle')}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        display: 'inline-flex', flex: 'none',
                                        color: 'var(--s-ink-3)', cursor: 'grab',
                                    }}
                                >
                                    <GripVertical size={15} strokeWidth={1.9} />
                                </span>
                            )}

                            {/* Baris OFF dipudarkan — teks sahaja, bukan seluruh baris,
                                supaya toggle dan menu kekal jelas untuk dihidupkan semula. */}
                            <div style={{ flex: 1, minWidth: 0, opacity: disorok ? 0.5 : 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <div className="s-cardname" style={{ minWidth: 0 }}>{a.data.nama}</div>
                                    {/* Hanya amaran yang dipapar — "Ready" pada setiap baris
                                        cuma bising (arahan user 2026-10-18). */}
                                    {!a.configured && <Badge tone="warn">{t('prostudio.wizard.notConfigured')}</Badge>}
                                </div>
                                <div className="s-dim" style={{ marginTop: 2 }}>{barisMeta}</div>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            flex: '0 0 auto', marginLeft: 'auto',
                        }}>
                            {/* Label platform — nama jenama (ChatGPT/Gemini), tidak diterjemah. */}
                            <span className="s-dim" style={{
                                fontWeight: 600, fontSize: 12, padding: '2px 9px', flex: 'none',
                                borderRadius: 999, border: '1px solid var(--s-line)',
                            }}>{a.data.platform === 'gemini' ? 'Gemini' : 'ChatGPT'}</span>

                            {/* Anak panah HANYA pada peranti sentuh (arahan user
                                2026-08-19). Pada komputer tarik-lepas sudah cukup, dan
                                dua butang tambahan pada setiap baris cuma menyesakkan
                                baris. Pada telefon pula ia satu-satunya laluan susun,
                                jadi ia tidak boleh digugurkan di sana — jangan ubah
                                syarat ini menjadi "bolehSusun" sahaja.
                                Kedua-duanya mati pada hujung senarai. */}
                            {bolehSusun && !adaTetikus && (
                                <>
                                    <IconBtn
                                        label={t('prostudio.wizard.moveUp')}
                                        icon={<ChevronUp size={15} strokeWidth={2} />}
                                        disabled={i === 0}
                                        onClick={(e) => { e.stopPropagation(); naikTurun(i, i - 1); }}
                                        style={i === 0 ? { opacity: 0.35, cursor: 'default' } : undefined}
                                    />
                                    <IconBtn
                                        label={t('prostudio.wizard.moveDown')}
                                        icon={<ChevronDown size={15} strokeWidth={2} />}
                                        disabled={i === agents.length - 1}
                                        onClick={(e) => { e.stopPropagation(); naikTurun(i, i + 1); }}
                                        style={i === agents.length - 1 ? { opacity: 0.35, cursor: 'default' } : undefined}
                                    />
                                </>
                            )}

                            {onTogolWorkspace && (
                                <button
                                    type="button" role="switch"
                                    aria-checked={!disorok}
                                    aria-label={t('prostudio.wizard.showInWs')}
                                    title={t('prostudio.wizard.showInWs')}
                                    className={disorok ? 's-switch' : 's-switch s-switch--on'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        /* Belum Configure: tiada tulisan — terangkan kenapa ia OFF. */
                                        if (!a.configured) { alert(t('prostudio.wizard.configureFirst')); return; }
                                        onTogolWorkspace(a.id, disorok);
                                    }}
                                >
                                    <span className="s-switch__dot" />
                                </button>
                            )}

                            {padam && (
                                <IconBtn
                                    label={t('prostudio.wizard.moreActions')}
                                    icon={<MoreHorizontal size={15} strokeWidth={1.9} />}
                                    onClick={(e) => bukaMenu(e, a.id)}
                                />
                            )}
                        </div>
                    </Row>
                );
            })}

            {menu && padam && (
                <>
                    <div
                        onClick={(e) => { e.stopPropagation(); setMenu(null); }}
                        style={{ position: 'fixed', inset: 0, zIndex: 205 }}
                    />
                    <div className="s-menu" style={{ left: menu.x, top: menu.y }}>
                        <button
                            type="button"
                            className="s-menu__item s-menu__item--stop"
                            onClick={(e) => { e.stopPropagation(); const id = menu.id; setMenu(null); padam(id); }}
                        >
                            <Trash2 size={14} strokeWidth={1.9} />
                            {t('prostudio.wizard.deleteAgent')}
                        </button>
                    </div>
                </>
            )}
        </List>
    );
}
